import { useEffect, useMemo, useState } from "react";
import { Form } from "reactstrap";
import Modal from "react-bootstrap/Modal";
import { APIMS } from "../../../../../../../../config/apims";
import { APISD } from "../../../../../../../../config/apisd";
import "bulma/css/bulma.css";
import "../../../../../../../../index.css";
import ToastError from "../../../../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../../../../NotificationToast/ToastSuccess";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

const css = `
  .ekskul-class-panel {
    margin-top: 8px;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 15px;
    background: #f8fafc;
  }

  .ekskul-class-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  .ekskul-class-toolbar strong {
    color: #1e293b;
    font-size: 13px;
  }

  .ekskul-class-toolbar span {
    color: #64748b;
    font-size: 12px;
  }

  .ekskul-class-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
  }

  .ekskul-class-item {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    min-height: 44px;
    padding: 10px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 11px;
    background: #ffffff;
    color: #475569;
    font-size: 13px;
    line-height: 1.35;
    cursor: pointer;
  }

  .ekskul-class-item:hover {
    border-color: #b9c8ef;
    background: #f8fbff;
  }

  .ekskul-class-item input {
    width: 15px;
    height: 15px;
    margin-top: 1px;
    accent-color: #3f5bea;
    cursor: pointer;
    flex: 0 0 auto;
  }

  .ekskul-class-item.is-disabled {
    opacity: 0.58;
    cursor: not-allowed;
    background: #f8fafc;
  }

  .ekskul-class-item.is-disabled input {
    cursor: not-allowed;
  }

  .ekskul-registered-badge {
    display: inline-flex;
    margin-left: 6px;
    padding: 2px 7px;
    border-radius: 999px;
    background: #fee2e2;
    color: #b91c1c;
    font-size: 10px;
    font-weight: 700;
    white-space: nowrap;
  }

  .ekskul-form-helper {
    color: #64748b;
    font-size: 12px;
    line-height: 1.55;
  }

  .ekskul-select-all {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #3158b5;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
  }

  .ekskul-select-all input {
    width: 14px;
    height: 14px;
    accent-color: #3f5bea;
  }

  @media (max-width: 768px) {
    .ekskul-class-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 576px) {
    .ekskul-class-panel {
      padding: 12px;
    }

    .ekskul-class-toolbar {
      align-items: flex-start;
      flex-direction: column;
    }

    .ekskul-class-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const getKelasId = (kelas) =>
  kelas?.id || kelas?.ysb_kelas_id || kelas?.kelas_id || "";

const getKelasName = (kelas) =>
  kelas?.name_kelas || kelas?.kelas_name || kelas?.name || "-";

const getGradeIdFromKelas = (kelas) =>
  kelas?.ysb_grade_level_id ||
  kelas?.grade_level_id ||
  kelas?.gradeLevelId ||
  kelas?.grade_level?.id ||
  kelas?.ysb_grade_level?.id ||
  "";

const getSchoolIdFromKelas = (kelas) =>
  kelas?.ysb_school_id ||
  kelas?.school_id ||
  kelas?.school_code ||
  kelas?.school?.id ||
  "";

const getBranchIdFromKelas = (kelas) =>
  kelas?.ysb_branch_id ||
  kelas?.branch_id ||
  kelas?.branch_code ||
  kelas?.branch ||
  "";

const isConfigKelasActive = (kelas) => {
  if (kelas?.state_form !== undefined && kelas?.state_form !== null) {
    return String(kelas.state_form) === "1";
  }

  const semesters = Array.isArray(kelas?.semesters) ? kelas.semesters : [];

  if (semesters.length > 0) {
    return semesters.some((semester) => String(semester?.state_form) === "1");
  }

  return true;
};

const toTitleCase = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

export default function ModalConfigEkskul(props) {
  const token = localStorage.getItem("token");
  const storageLevel = localStorage.getItem("level") || "developer";

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ekstrakurikulerList, setEkstrakurikulerList] = useState([]);
  const [kelasList, setKelasList] = useState([]);
  const [form, setForm] = useState({
    ysb_matpel_id: "",
    ysb_kelas_id: [],
  });

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  const selectedUserId = (
    localStorage.getItem("id_admin") ||
    localStorage.getItem("user_id") ||
    localStorage.getItem("id") ||
    ""
  ).replace(/[|&;$%@"<>()+,]/g, "");

  useEffect(() => {
    if (!props?.show) return undefined;

    let isActive = true;

    setForm({
      ysb_matpel_id: "",
      ysb_kelas_id: [],
    });
    setEkstrakurikulerList([]);
    setKelasList([]);

    const fetchModalData = async () => {
      if (!props?.branchId || !props?.schoolId || !props?.gradeLevelId) {
        return;
      }

      setLoading(true);

      try {
        const ekstrakurikulerPayload = {
          page: 1,
          limit: 10000,
          ascending: true,
          ysb_branch_id: props.branchId,
        };

        const [ekstrakurikulerResponse, kelasResponse] = await Promise.all([
          APISD.get("/api/ekstrakurikuler", {
            ...fetchParams,
            params: ekstrakurikulerPayload,
            data: ekstrakurikulerPayload,
          }),
          APIMS.get(
            `/api/kelas?page=1&limit=10000&ascending=1&branch=${props.branchId}&level=${storageLevel}`,
            fetchParams
          ),
        ]);

        if (!isActive) return;

        const ekstrakurikulerRows = Array.isArray(
          ekstrakurikulerResponse?.data?.data
        )
          ? ekstrakurikulerResponse.data.data
          : [];

        const kelasRows = Array.isArray(kelasResponse?.data?.data)
          ? kelasResponse.data.data
          : [];

        setEkstrakurikulerList(
          [...ekstrakurikulerRows].sort((a, b) =>
            String(a?.name_matpel || "").localeCompare(
              String(b?.name_matpel || ""),
              "id",
              { numeric: true, sensitivity: "base" }
            )
          )
        );

        setKelasList(
          kelasRows
            .filter((kelas) => {
              const branchId = getBranchIdFromKelas(kelas);
              const schoolId = getSchoolIdFromKelas(kelas);
              const gradeLevelId = getGradeIdFromKelas(kelas);

              const branchMatch =
                !branchId || String(branchId) === String(props.branchId);
              const schoolMatch =
                !schoolId || String(schoolId) === String(props.schoolId);
              const gradeMatch =
                String(gradeLevelId) === String(props.gradeLevelId);

              return branchMatch && schoolMatch && gradeMatch;
            })
            .sort((a, b) =>
              String(getKelasName(a)).localeCompare(
                String(getKelasName(b)),
                "id",
                { numeric: true, sensitivity: "base" }
              )
            )
        );
      } catch (error) {
        if (!isActive) return;

        console.error("Gagal mengambil data form ekstrakurikuler:", error);
        setEkstrakurikulerList([]);
        setKelasList([]);

        ToastError.fire({
          icon: "error",
          title:
            error?.response?.data?.message ||
            "Gagal mengambil data ekstrakurikuler dan kelas",
        });
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchModalData();

    return () => {
      isActive = false;
    };
  }, [
    props?.show,
    props?.branchId,
    props?.schoolId,
    props?.gradeLevelId,
    storageLevel,
    fetchParams,
  ]);

  const isKelasAlreadyRegistered = (kelasId) => {
    if (!form.ysb_matpel_id || !kelasId) return false;

    return (props?.existingConfigMatpels || []).some((ekskul) => {
      const ekskulId = ekskul?.ysb_matpel_id || ekskul?.id || "";

      if (String(ekskulId) !== String(form.ysb_matpel_id)) return false;

      return (ekskul?.grade_levels || []).some((gradeLevel) => {
        const gradeLevelId =
          gradeLevel?.ysb_grade_level_id || gradeLevel?.id || "";

        if (String(gradeLevelId) !== String(props?.gradeLevelId)) return false;

        return (gradeLevel?.kelas || []).some((kelas) => {
          const registeredKelasId =
            kelas?.ysb_kelas_id || kelas?.kelas_id || kelas?.id || "";
          const registeredSchoolId = getSchoolIdFromKelas(kelas);

          const schoolMatch =
            !registeredSchoolId ||
            String(registeredSchoolId) === String(props?.schoolId);

          return (
            schoolMatch &&
            String(registeredKelasId) === String(kelasId) &&
            isConfigKelasActive(kelas)
          );
        });
      });
    });
  };

  const selectableKelasIds = kelasList
    .map((kelas) => getKelasId(kelas))
    .filter((kelasId) => kelasId && !isKelasAlreadyRegistered(kelasId));

  const allSelectableChecked =
    selectableKelasIds.length > 0 &&
    selectableKelasIds.every((kelasId) =>
      form.ysb_kelas_id.some(
        (selectedId) => String(selectedId) === String(kelasId)
      )
    );

  const handleChangeEkstrakurikuler = (event) => {
    const value = event.target.value;

    setForm({
      ysb_matpel_id: value,
      ysb_kelas_id: [],
    });
  };

  const handleCheckKelas = (kelasId) => {
    if (!kelasId || isKelasAlreadyRegistered(kelasId)) return;

    setForm((prev) => {
      const isChecked = prev.ysb_kelas_id.some(
        (selectedId) => String(selectedId) === String(kelasId)
      );

      return {
        ...prev,
        ysb_kelas_id: isChecked
          ? prev.ysb_kelas_id.filter(
              (selectedId) => String(selectedId) !== String(kelasId)
            )
          : [...prev.ysb_kelas_id, kelasId],
      };
    });
  };

  const handleCheckAll = () => {
    if (!form.ysb_matpel_id || selectableKelasIds.length === 0) return;

    setForm((prev) => ({
      ...prev,
      ysb_kelas_id: allSelectableChecked ? [] : selectableKelasIds,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      !props?.academicYearId ||
      !props?.branchId ||
      !props?.schoolId ||
      !props?.gradeLevelId
    ) {
      ToastError.fire({
        icon: "error",
        title: "Filter tahun ajaran, cabang, sekolah, dan tingkatan wajib lengkap",
      });
      return;
    }

    if (!form.ysb_matpel_id) {
      ToastError.fire({
        icon: "error",
        title: "Ekstrakurikuler wajib dipilih",
      });
      return;
    }

    if (form.ysb_kelas_id.length === 0) {
      ToastError.fire({
        icon: "error",
        title: "Pilih minimal satu kelas",
      });
      return;
    }

    if (!selectedUserId) {
      ToastError.fire({
        icon: "error",
        title: "User ID belum tersedia. Silakan login ulang.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ysb_academic_year_id: props.academicYearId,
        ysb_branch_id: props.branchId,
        ysb_school_id: props.schoolId,
        ysb_grade_level_id: props.gradeLevelId,
        ysb_matpel_id: [form.ysb_matpel_id],
        ysb_kelas_id: form.ysb_kelas_id,
        user_id: selectedUserId,
      };

      const response = await APISD.post(
        "/api/ekstrakurikuler-configs/store",
        payload,
        fetchParams
      );

      const isSuccess =
        response?.status === 200 ||
        response?.status === 201 ||
        response?.data?.status === 200 ||
        response?.data?.status === 201;

      if (!isSuccess || response?.data?.error === true) {
        throw new Error(
          response?.data?.message || "Gagal menyimpan konfigurasi ekstrakurikuler"
        );
      }

      ToastSuccess.fire({
        icon: "success",
        title:
          response?.data?.message ||
          "Konfigurasi ekstrakurikuler berhasil disimpan",
      });

      setForm({
        ysb_matpel_id: "",
        ysb_kelas_id: [],
      });

      props?.onHide?.();
      await props?.GetResponseData?.();
    } catch (error) {
      console.error("Gagal menyimpan konfigurasi ekstrakurikuler:", error);

      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          error?.message ||
          "Gagal menyimpan konfigurasi ekstrakurikuler",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      {...props}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="module-modal-dialog module-modal-dialog-lg"
      contentClassName="module-modal-content"
    >
      <style>{css}</style>

      <Form onSubmit={handleSubmit}>
        <div className="module-modal-header">
          <div>
            <h5 className="module-modal-title">Tambah Ekskul Kelas</h5>
            {/* <p className="module-modal-subtitle">
              Pilih ekstrakurikuler dan kelas sesuai filter yang aktif.
            </p> */}
          </div>

          <button
            type="button"
            className="module-modal-close"
            onClick={props.onHide}
            disabled={isSubmitting}
            aria-label="Tutup modal"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <Modal.Body className="module-modal-body mt-1 mx-1">
          <div className="module-form-grid">
            <div className="module-form-group">
              <label className="module-form-label">Tahun Ajaran</label>
              <select
                className="module-form-select"
                value={props?.academicYearId || ""}
                disabled
              >
                <option value={props?.academicYearId || ""}>
                  {props?.academicYearName || "Tahun ajaran belum dipilih"}
                </option>
              </select>
            </div>

            <div className="module-form-group">
              <label className="module-form-label">Cabang</label>
              <select
                className="module-form-select"
                value={props?.branchId || ""}
                disabled
              >
                <option value={props?.branchId || ""}>
                  {props?.branchName || props?.branchId || "Cabang belum dipilih"}
                </option>
              </select>
            </div>

            <div className="module-form-group">
              <label className="module-form-label">Sekolah</label>
              <select
                className="module-form-select"
                value={props?.schoolId || ""}
                disabled
              >
                <option value={props?.schoolId || ""}>
                  {props?.schoolName || props?.schoolId || "Sekolah belum dipilih"}
                </option>
              </select>
            </div>

            <div className="module-form-group">
              <label className="module-form-label">Tingkatan</label>
              <select
                className="module-form-select"
                value={props?.gradeLevelId || ""}
                disabled
              >
                <option value={props?.gradeLevelId || ""}>
                  {props?.gradeLevelName
                    ? `Tingkatan ${props.gradeLevelName}`
                    : "Tingkatan belum dipilih"}
                </option>
              </select>
            </div>

            <div className="module-form-group module-form-span-2">
              <label className="module-form-label" htmlFor="add-ekstrakurikuler">
                Ekstrakurikuler <span>*</span>
              </label>

              <select
                id="add-ekstrakurikuler"
                className="module-form-select"
                value={form.ysb_matpel_id}
                onChange={handleChangeEkstrakurikuler}
                disabled={loading || isSubmitting}
                required
              >
                <option value="" hidden>
                  {loading ? "Memuat ekstrakurikuler..." : "Pilih Ekstrakurikuler"}
                </option>

                {ekstrakurikulerList.map((ekskul, index) => (
                  <option key={ekskul?.id || index} value={ekskul?.id}>
                    {ekskul?.name_matpel || "-"}
                  </option>
                ))}
              </select>
            </div>

            <div className="module-form-group module-form-span-2">
              <label className="module-form-label">
                Kelas <span>*</span>
              </label>

              <div className="ekskul-class-panel">
                {loading ? (
                  <div className="ekskul-form-helper">Memuat data kelas...</div>
                ) : !form.ysb_matpel_id ? (
                  <div className="ekskul-form-helper">
                    Pilih ekstrakurikuler terlebih dahulu.
                  </div>
                ) : kelasList.length === 0 ? (
                  <div className="ekskul-form-helper">
                    Kelas tidak ditemukan pada sekolah dan tingkatan ini.
                  </div>
                ) : (
                  <>
                    <div className="ekskul-class-toolbar">
                      <div>
                        <strong>Pilih kelas tujuan</strong>
                        <br />
                        <span>{kelasList.length} kelas tersedia</span>
                      </div>

                      <label className="ekskul-select-all">
                        <input
                          type="checkbox"
                          checked={allSelectableChecked}
                          disabled={
                            isSubmitting || selectableKelasIds.length === 0
                          }
                          onChange={handleCheckAll}
                        />
                        Pilih semua
                      </label>
                    </div>

                    <div className="ekskul-class-grid">
                      {kelasList.map((kelas, index) => {
                        const kelasId = getKelasId(kelas);
                        const alreadyRegistered =
                          isKelasAlreadyRegistered(kelasId);
                        const checked = form.ysb_kelas_id.some(
                          (selectedId) =>
                            String(selectedId) === String(kelasId)
                        );

                        return (
                          <label
                            key={kelasId || index}
                            className={`ekskul-class-item ${
                              alreadyRegistered ? "is-disabled" : ""
                            }`}
                            title={
                              alreadyRegistered
                                ? "Ekstrakurikuler ini sudah terdaftar di kelas tersebut"
                                : ""
                            }
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={alreadyRegistered || isSubmitting}
                              onChange={() => handleCheckKelas(kelasId)}
                            />
                            <span>
                              {props?.gradeLevelName || ""}{" "}
                              {toTitleCase(getKelasName(kelas))}
                              {alreadyRegistered && (
                                <span className="ekskul-registered-badge">
                                  Sudah terdaftar
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>

        <div className="module-modal-footer">
          <button
            type="button"
            className="module-modal-cancel"
            onClick={props.onHide}
            disabled={isSubmitting}
          >
            Batal
          </button>

          <button
            type="submit"
            className="module-modal-submit"
            disabled={
              loading ||
              isSubmitting ||
              !form.ysb_matpel_id ||
              form.ysb_kelas_id.length === 0
            }
          >
            {isSubmitting && <span className="module-button-spinner" />}
            {isSubmitting ? "Menyimpan..." : "Tambahkan"}
          </button>
        </div>
      </Form>
    </Modal>
  );
}
